import { create } from "zustand";
import { fetchApi, getLoginUrl, saveAuthToken } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import {
  MyOrganizationsResponseSchema,
  SwitchOrganizationResponseSchema,
} from "@forgepoint/shared";
import type {
  OrganizationMembership,
  SwitchOrganizationRequest,
} from "@forgepoint/shared";
import {
  isSsoRequiredResponse,
  SwitchOrganizationError,
  toSwitchOrganizationError,
} from "@/modules/organizations/errors";

/** Per-organization switch loading/error flags, keyed by organization id. */
export interface OrganizationSwitchState {
  isLoadingSwitch: boolean;
  switchError: string | null;
}

interface OrganizationMembershipsStore {
  /** Map of organizationId -> membership, in the order the API returned them. */
  membershipMap: Map<string, OrganizationMembership>;
  isLoadingList: boolean;
  listError: string | null;
  /** Per-organization switch states, keyed by organization id. */
  switchStates: Record<string, OrganizationSwitchState>;

  fetchMemberships: () => Promise<void>;
  /**
   * Moves the session to another organization and reloads the app into it, so
   * every org-scoped store re-initializes from scratch in the destination org.
   */
  switchOrganization: (organizationId: string) => Promise<void>;
}

/** The caller's active organization memberships, backed by the users and auth endpoints. */
export const useOrganizationMembershipsStore =
  create<OrganizationMembershipsStore>((set) => ({
    membershipMap: new Map(),
    isLoadingList: false,
    listError: null,
    switchStates: {},

    fetchMemberships: async () => {
      set({ isLoadingList: true, listError: null });
      try {
        const response = await fetchApi("users/me/organizations", "GET");
        if (!response.ok) {
          set({
            listError: response.error ?? "Failed to load organizations",
            isLoadingList: false,
          });
          return;
        }
        const data = MyOrganizationsResponseSchema.parse(response.data);
        set({
          membershipMap: new Map(
            data.organizations.map((membership) => [
              membership.organizationId,
              membership,
            ]),
          ),
          isLoadingList: false,
        });
      } catch (error) {
        if (error instanceof Error) {
          captureException(error);
        }
        set({
          listError: "Failed to load organizations",
          isLoadingList: false,
        });
      }
    },

    switchOrganization: async (organizationId) => {
      set((state) => ({
        switchStates: {
          ...state.switchStates,
          [organizationId]: { isLoadingSwitch: true, switchError: null },
        },
      }));

      try {
        const body: SwitchOrganizationRequest = { organizationId };
        // Credentials are required for the browser to accept the cross-origin
        // last-org cookie this endpoint sets on both success and sso_required.
        const response = await fetchApi("auth/session/organization", "PUT", {
          body,
          credentials: "include",
        });

        if (isSsoRequiredResponse(response.status, response.error)) {
          window.location.href = getLoginUrl();
          return;
        }

        if (!response.ok) {
          throw toSwitchOrganizationError({
            status: response.status,
            error: response.error,
          });
        }

        const { sessionToken } = SwitchOrganizationResponseSchema.parse(
          response.data,
        );
        saveAuthToken(sessionToken);
        window.location.reload();
      } catch (error) {
        const failure =
          error instanceof SwitchOrganizationError
            ? error
            : toSwitchOrganizationError(null);
        if (
          !(error instanceof SwitchOrganizationError) &&
          error instanceof Error
        ) {
          captureException(error);
        }
        set((state) => ({
          switchStates: {
            ...state.switchStates,
            [organizationId]: {
              isLoadingSwitch: false,
              switchError: failure.message,
            },
          },
        }));
      }
    },
  }));
